import {
  Col,
  Form,
  Input,
  Row,
  theme,
  Select,
  Divider,
  Typography,
  message,
  notification,
  Checkbox,
  Card,
  Tag,
} from "antd";
import EditQty from "./EditQty";
import { useEffect, useState, useMemo } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router";
import EOLService from "../../services/EOLService";
import LoadingSpin from "../loadingSpin/LoadingSpin";
import ResponseMessage from "../Comman/Notification/ResponseMessge";
import "../../styles/global-ui.css";

const ViewKanban = ({ userData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [dispatchArea, setDispatchArea] = useState();
  const [kbNumber, setKbNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [getKanbanData, setKanbanData] = useState([]);
  const [api, contextHolder] = notification.useNotification();
  const [BinOptions, setBinOptions] = useState([]);
  const [selectedBin, setSelectedBin] = useState(null);
  const [binFull, setBinFull] = useState("no");
  const [selectedLine, setSelectedLine] = useState(null);
  const [lineOptions, setLineOptions] = useState([]);

  const openNotificationWithIcon = (type, title, description = "") => {
    api[type]({
      message: title,
      description,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      const queryParams = new URLSearchParams(location.search);
      const kbNumValue = queryParams.get("kb_num");
      setKbNumber(kbNumValue || "");

      if (!kbNumValue) {
        ResponseMessage.error({ message: "No Kanban number provided." });
        setKanbanData([]);
        return;
      }

      setIsLoading(true);

      try {
        const [kanbanResponse, binResponse, lineResponse] = await Promise.all([
          EOLService.getKanbanDataById({ kb_number: kbNumValue }),
          EOLService.getAvailableBins(),
          EOLService.getLines(),
        ]);

        if (binResponse?.status && Array.isArray(binResponse?.data)) {
          setBinOptions(
            binResponse.data.map((item) => ({
              value: item.bin_id,
              label: item.bin_id,
            }))
          );
        } else {
          setBinOptions([]);
          openNotificationWithIcon("warning", "Unable to fetch bin options.");
        }

        if (lineResponse?.status && Array.isArray(lineResponse?.line_no)) {
          setLineOptions(
            lineResponse.line_no.map((line) => ({
              value: line,
              label: line,
            }))
          );
        } else {
          setLineOptions([]);
          openNotificationWithIcon("warning", "Unable to fetch line options.");
        }

        if (Array.isArray(kanbanResponse) && kanbanResponse.length > 0) {
          setKanbanData(kanbanResponse);
        } else if (
          Array.isArray(kanbanResponse?.data) &&
          kanbanResponse.data.length > 0
        ) {
          setKanbanData(kanbanResponse.data);
        } else {
          setKanbanData([]);
          openNotificationWithIcon("warning", "No Kanban data available.");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setKanbanData([]);
        openNotificationWithIcon(
          "error",
          "An error occurred while fetching data",
          error?.message || ""
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [location]);

  const kanbanData1 = useMemo(() => {
    return Array.isArray(getKanbanData) && getKanbanData.length > 0
      ? getKanbanData.map((item) => ({
          kanbanNo: item.kb_number,
          poNo: item.po_no,
          quantity: item.quantity,
          color: item.color,
          itemNo: item.item_no,
          country: item.country,
          totalQty: item.tot_qty,
        }))
      : [];
  }, [getKanbanData]);

  useEffect(() => {
    if (kanbanData1.length > 0) {
      form.setFieldsValue({
        kanbanNo: kanbanData1[0].kanbanNo,
        poNo: kanbanData1[0].poNo,
        color: kanbanData1[0].color,
        itemNo: kanbanData1[0].itemNo,
        country: kanbanData1[0].country,
      });
    } else {
      form.resetFields();
    }
  }, [kanbanData1, form]);

  const sizeCollection = useMemo(() => {
    return Array.isArray(getKanbanData)
      ? getKanbanData
          .map((item) => {
            let sizes = {};
            let totals = {};

            try {
              sizes = JSON.parse(item.size_name || "{}");
            } catch (error) {
              console.error("Invalid size_name JSON:", item.size_name);
            }

            try {
              totals = JSON.parse(item.tot_qty || "{}");
            } catch (error) {
              console.error("Invalid tot_qty JSON:", item.tot_qty);
            }

            return Object.keys(sizes)
              .map((size, idx) => {
                const availableQty = sizes[size] || "0";
                const totalQty = totals[size] || "0";

                if (availableQty === "0" && totalQty === "0") {
                  return null;
                }

                return {
                  key: `${idx + 1}`,
                  size,
                  availableQty,
                  totalQty,
                  kanban_no: item.kb_number,
                };
              })
              .filter(Boolean);
          })
          .flat()
      : [];
  }, [getKanbanData]);

  const handleArrowClick = () => {
    navigate(-1);
  };

  const DispatchAreaOptions = [
    { id: 1, name: "Washing", label: "Washing", value: "Washing" },
    { id: 2, name: "Hold Area", label: "Hold Area", value: "Hold Area" },
    { id: 3, name: "Finishing", label: "Finishing", value: "Finishing" },
  ];

  const handleBinChange = (value) => {
    setSelectedBin(value);
    message.success(`Bin ${value} Selected`);
  };

  const onChangeCheckBox = (e) => {
    const isChecked = e.target.checked;
    setBinFull(isChecked ? "yes" : "no");

    if (!isChecked) {
      setSelectedLine(null);
    }
  };

  const handleLineChange = (value) => {
    setSelectedLine(value);
  };

  return (
    <div className="page-container" style={{ position: "relative" }}>
      {contextHolder}

      {isLoading && (
        <div className="dashboard-overlay-loader">
          <LoadingSpin />
        </div>
      )}

      <div className="page-header">
        <div className="page-title-wrap">
          <Typography.Title level={3} className="page-title">
            <ArrowLeftOutlined
              onClick={handleArrowClick}
              style={{ marginRight: 12, cursor: "pointer" }}
            />
            End Of Line Dispatch
          </Typography.Title>
          <Typography.Text className="page-subtitle">
            Kanban details and dispatch entry
          </Typography.Text>
        </div>

        {kbNumber ? (
          <Tag className="filter-tag" color="blue">
            Kanban: {kbNumber}
          </Tag>
        ) : null}
      </div>

      <Divider className="page-divider" />

      {getKanbanData?.length > 0 ? (
        <>
          <div className="compact-stats" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 8]} justify="center">
              <Col xs={12} sm={8}>
                <div className="compact-stat-item">
                  <div className="compact-stat-value" style={{ color: "#2563eb" }}>
                    {sizeCollection.length}
                  </div>
                  <div className="compact-stat-label">Sizes</div>
                </div>
              </Col>

              <Col xs={12} sm={8}>
                <div className="compact-stat-item">
                  <div className="compact-stat-value" style={{ color: "#0f766e" }}>
                    {kanbanData1?.[0]?.kanbanNo || "-"}
                  </div>
                  <div className="compact-stat-label">Kanban No</div>
                </div>
              </Col>

              <Col xs={12} sm={8}>
                <div className="compact-stat-item">
                  <div className="compact-stat-value" style={{ color: "#7c3aed" }}>
                    {kanbanData1?.[0]?.itemNo || "-"}
                  </div>
                  <div className="compact-stat-label">Item No</div>
                </div>
              </Col>
            </Row>
          </div>
<Card className="modern-card eol-main-card">
  <Form form={form} name="simple_search" layout="vertical" onFinish={() => {}}>
    <Row gutter={[20, 20]}>
      <Col xs={24} lg={14}>
        <div className="eol-section-card">
          <div className="eol-section-title">Kanban Details</div>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item name="kanbanNo" label="Kanban No">
                <Input className="eol-input" readOnly />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="itemNo" label="Item No">
                <Input className="eol-input" readOnly />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="poNo" label="PO No">
                <Input className="eol-input" readOnly />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="color" label="Color">
                <Input className="eol-input" readOnly />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item name="country" label="Country">
                <Input className="eol-input" readOnly />
              </Form.Item>
            </Col>
          </Row>
        </div>
      </Col>

      <Col xs={24} lg={10}>
        <div className="eol-section-card eol-dispatch-card">
          <div className="eol-section-title">Dispatch Entry</div>

          <Form.Item label="Dispatch To" name="dispatchTo">
            <Select
              className="eol-select"
              options={DispatchAreaOptions}
              value={dispatchArea}
              onChange={(value) => {
                setDispatchArea(value);
                setSelectedBin(null);
                setBinFull("no");
                setSelectedLine(null);
              }}
              placeholder="Select dispatch area"
            />
          </Form.Item>

          {dispatchArea === "Washing" && (
            <>
              <Form.Item label="Bin">
                <Select
                  className="eol-select"
                  value={selectedBin}
                  onChange={handleBinChange}
                  placeholder="Select Bin"
                  options={BinOptions}
                />
              </Form.Item>

              <div className="eol-check-card">
                <Checkbox checked={binFull === "yes"} onChange={onChangeCheckBox}>
                  Bin Full
                </Checkbox>
              </div>

              {binFull === "yes" && (
                <Form.Item label="Line">
                  <Select
                    className="eol-select"
                    value={selectedLine}
                    onChange={handleLineChange}
                    placeholder="Select line"
                    options={lineOptions}
                  />
                </Form.Item>
              )}
            </>
          )}
        </div>
      </Col>

      <Col xs={24}>
        <div className="eol-section-card eol-qty-card">
          <div className="eol-section-title">Quantity Entry</div>

          <EditQty
            binFull={binFull}
            selectedBin={selectedBin}
            dispatchArea={dispatchArea}
            userData={userData}
            handleArrowClick={handleArrowClick}
            editValue={sizeCollection}
            selectedLine={selectedLine}
          />
        </div>
      </Col>
    </Row>
  </Form>
</Card>
        </>
      ) : (
        <Card className="modern-card">
          <div className="no-data-wrap">
            <div className="no-data-text">No Kanban Data Found</div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ViewKanban;